import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpenIcon, TrendingUpIcon, CheckCircleIcon, HomeIcon, ArrowRightIcon, FileTextIcon, SparklesIcon, CalendarIcon, ReceiptIcon, LayoutDashboardIcon, BotIcon, TrophyIcon, StarIcon, GraduationCapIcon, HeadphonesIcon, LockIcon, PlayCircleIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useProgress } from '../hooks/useProgress';
import { useTransactions } from '../contexts/TransactionContext';
import { coursesStructure } from '../data/courseData';
import { coursesData } from '../data/coursesData';
import { BadgesSection } from '../components/BadgesSection';
import { CertificatesSection } from '../components/CertificatesSection';
export function Dashboard() {
  const {
    user
  } = useAuth();
  const {
    getOverallProgress,
    getCourseProgress,
    completedLessons,
    calculatePoints
  } = useProgress();
  const {
    transactions,
    getMonthlyStats
  } = useTransactions();
  if (!user) {
    return <div className="w-full min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center" data-id="element-8204">
        <div className="text-center" data-id="element-8205">
          <div className="w-16 h-16 border-4 border-[#357BEB] border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-8206"></div>
          <p className="text-gray-600" data-id="element-8207">Se încarcă...</p>
        </div>
      </div>;
  }
  // Progress calculations
  let progress = 0;
  let completedLessonsCount = 0;
  let totalLessons = 1;
  try {
    const progressData = getOverallProgress();
    if (progressData) {
      completedLessonsCount = progressData.completedLessons || 0;
      totalLessons = progressData.totalLessons || 1;
      progress = Math.round(completedLessonsCount / totalLessons * 100);
    }
  } catch (error) {
    console.error('Error calculating progress:', error);
  }
  // Calculate points
  const pointsBreakdown = calculatePoints();
  // Find current lesson (last incomplete lesson)
  const findCurrentLesson = () => {
    // Go through courses in order
    for (const courseInfo of coursesData) {
      if (courseInfo.status !== 'active') continue;
      const course = coursesStructure.find(c => c.id === courseInfo.id);
      if (!course) continue;
      // Go through modules in order
      for (const module of course.modules) {
        // Go through lessons in order
        for (const lesson of module.lessons) {
          // Check if this lesson is completed
          const isComplete = completedLessons.some(p => p.courseId === courseInfo.id && p.moduleId === module.id && p.lessonId === lesson.id);
          // If not completed, this is the current lesson
          if (!isComplete) {
            return {
              courseId: courseInfo.id,
              courseTitle: courseInfo.title,
              moduleId: module.id,
              moduleTitle: module.title,
              lessonId: lesson.id,
              lessonTitle: lesson.title
            };
          }
        }
      }
    }
    // All lessons completed
    return null;
  };
  const currentLesson = findCurrentLesson();
  // Check module unlock status
  const course1Progress = getCourseProgress('concept-clc', 6);
  const hasCompletedCourse1 = course1Progress === 100;
  const hasCompletedOnboarding = user ? !!(user.fullName && user.phone && user.dateOfBirth && user.city && user.address) : false;
  const course2Progress = getCourseProgress('proceduri-inchiriere', 15);
  const hasCompletedCourse2 = course2Progress === 100;
  const course3Progress = getCourseProgress('proceduri-vanzare', 20);
  const hasCompletedCourse3 = course3Progress === 100;
  const rentalModuleUnlocked = hasCompletedCourse1 && hasCompletedOnboarding && hasCompletedCourse2;
  const saleModuleUnlocked = hasCompletedCourse1 && hasCompletedOnboarding && hasCompletedCourse2 && hasCompletedCourse3;
  const monthlyStats = getMonthlyStats();
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50" data-id="element-8208">
      {/* Hero Section - Mobile Optimized */}
      <section className="relative bg-gradient-to-br from-[#132C53] to-[#1e4278] text-white pt-16 pb-12 sm:pt-20 sm:pb-16 overflow-hidden" data-id="element-8209">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-8210"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-8211">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex flex-col sm:flex-row items-center sm:items-center space-y-4 sm:space-y-0 sm:space-x-6" data-id="element-8212">
            {user.photo && <motion.img initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.2,
            type: 'spring',
            stiffness: 200
          }} src={user.photo} alt={user.fullName || 'User'} className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover border-4 border-white/20 shadow-xl" data-id="element-8213" />}
            <div className="text-center sm:text-left" data-id="element-8214">
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3
            }} className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full mb-2" data-id="element-8215">
                <SparklesIcon className="w-4 h-4 text-yellow-400" data-id="element-8216" />
                <span className="text-sm font-medium" data-id="element-8217">Dashboard</span>
              </motion.div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold mb-2" data-id="element-8218">
                Bine ai revenit, {user.fullName?.split(' ')[0] || 'Agent'}!
              </h1>
              <p className="text-gray-300 text-sm sm:text-base md:text-lg" data-id="element-8219">
                Continuă-ți parcursul de dezvoltare profesională
              </p>
            </div>
          </motion.div>
        </div>

        <div className="absolute -top-10 -right-10 w-40 h-40 sm:w-64 sm:h-64 bg-[#357BEB] rounded-full blur-3xl opacity-20" data-id="element-8220"></div>
        <div className="absolute -bottom-10 -left-10 w-40 h-40 sm:w-64 sm:h-64 bg-[#21A366] rounded-full blur-3xl opacity-20" data-id="element-8221"></div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 sm:-mt-8 pb-12 sm:pb-16" data-id="element-8222">
        {/* Points Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-2xl border-2 border-amber-300 mb-6 sm:mb-8 relative overflow-hidden" data-id="element-8223">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl" data-id="element-8224"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-2xl" data-id="element-8225"></div>

          <div className="relative z-10" data-id="element-8226">
            <div className="flex items-center justify-between mb-4" data-id="element-8227">
              <div className="flex items-center space-x-3" data-id="element-8228">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center" data-id="element-8229">
                  <TrophyIcon className="w-7 h-7 text-white" data-id="element-8230" />
                </div>
                <div data-id="element-8231">
                  <h2 className="text-xl sm:text-2xl font-bold text-white" data-id="element-8232">
                    Punctaj Total
                  </h2>
                  <p className="text-white/80 text-sm" data-id="element-8233">Progres gamificat</p>
                </div>
              </div>
              <div className="text-right" data-id="element-8234">
                <p className="text-4xl sm:text-5xl font-black text-white drop-shadow-lg" data-id="element-8235">
                  {pointsBreakdown.totalPoints}
                </p>
                <p className="text-white/80 text-sm font-medium" data-id="element-8236">puncte</p>
              </div>
            </div>

            {/* Points Breakdown */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-8237">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30" data-id="element-8238">
                <div className="flex items-center space-x-2 mb-2" data-id="element-8239">
                  <StarIcon className="w-5 h-5 text-yellow-300" data-id="element-8240" />
                  <p className="text-white/90 text-sm font-medium" data-id="element-8241">Lecții</p>
                </div>
                <p className="text-2xl font-bold text-white" data-id="element-8242">
                  {pointsBreakdown.lessonPoints}
                </p>
                <p className="text-white/70 text-xs mt-1" data-id="element-8243">
                  {completedLessonsCount} × 10 puncte
                </p>
              </div>

              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30" data-id="element-8244">
                <div className="flex items-center space-x-2 mb-2" data-id="element-8245">
                  <CheckCircleIcon className="w-5 h-5 text-green-300" data-id="element-8246" />
                  <p className="text-white/90 text-sm font-medium" data-id="element-8247">Module</p>
                </div>
                <p className="text-2xl font-bold text-white" data-id="element-8248">
                  {pointsBreakdown.moduleBonus}
                </p>
                <p className="text-white/70 text-xs mt-1" data-id="element-8249">
                  {pointsBreakdown.moduleBonus / 25} × 25 puncte bonus
                </p>
              </div>

              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30" data-id="element-8250">
                <div className="flex items-center space-x-2 mb-2" data-id="element-8251">
                  <TrophyIcon className="w-5 h-5 text-amber-300" data-id="element-8252" />
                  <p className="text-white/90 text-sm font-medium" data-id="element-8253">
                    Training-uri
                  </p>
                </div>
                <p className="text-2xl font-bold text-white" data-id="element-8254">
                  {pointsBreakdown.courseBonus}
                </p>
                <p className="text-white/70 text-xs mt-1" data-id="element-8255">
                  {pointsBreakdown.courseBonus / 100} × 100 puncte bonus
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Progress Overview Card - Redesigned with gradient style */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-gradient-to-br from-[#357BEB] via-[#2868d8] to-[#1e4278] rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-2xl border-2 border-blue-300 mb-6 sm:mb-8 relative overflow-hidden" data-id="element-8256">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl" data-id="element-8257"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-2xl" data-id="element-8258"></div>

          <div className="relative z-10" data-id="element-8259">
            <div className="flex items-center justify-between mb-6" data-id="element-8260">
              <div className="flex items-center space-x-3" data-id="element-8261">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center" data-id="element-8262">
                  <GraduationCapIcon className="w-7 h-7 text-white" data-id="element-8263" />
                </div>
                <div data-id="element-8264">
                  <h2 className="text-xl sm:text-2xl font-bold text-white" data-id="element-8265">
                    Progres Training
                  </h2>
                  <p className="text-white/80 text-sm" data-id="element-8266">
                    {completedLessonsCount} din {totalLessons} lecții completate
                  </p>
                </div>
              </div>
              <div className="text-right" data-id="element-8267">
                <p className="text-4xl sm:text-5xl font-black text-white drop-shadow-lg" data-id="element-8268">
                  {progress}%
                </p>
                <p className="text-white/80 text-sm font-medium" data-id="element-8269">completat</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="bg-white/20 backdrop-blur-sm rounded-full h-4 overflow-hidden mb-6 border border-white/30" data-id="element-8270">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${progress}%`
            }} transition={{
              duration: 1,
              delay: 0.5
            }} className="bg-gradient-to-r from-[#21A366] to-[#1a8553] h-full rounded-full shadow-lg" data-id="element-8271" />
            </div>

            {/* Current Lesson Info */}
            {currentLesson && <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30" data-id="element-8272">
                <div className="flex items-start space-x-3" data-id="element-8273">
                  <div className="flex-shrink-0 w-10 h-10 bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center" data-id="element-8274">
                    <BookOpenIcon className="w-5 h-5 text-white" data-id="element-8275" />
                  </div>
                  <div className="flex-1" data-id="element-8276">
                    <p className="text-xs text-white/80 mb-1 font-medium" data-id="element-8277">
                      Lecția curentă
                    </p>
                    <h3 className="font-bold text-white mb-1" data-id="element-8278">
                      {currentLesson.lessonTitle}
                    </h3>
                    <p className="text-sm text-white/80 mb-3" data-id="element-8279">
                      {currentLesson.courseTitle} • {currentLesson.moduleTitle}
                    </p>
                    <Link to={`/curs/${currentLesson.courseId}/modul/${currentLesson.moduleId}/lectie/${currentLesson.lessonId}`} className="inline-flex items-center space-x-2 bg-white text-[#357BEB] px-4 py-2 rounded-lg text-sm font-semibold hover:bg-white/90 transition-colors shadow-lg" data-id="element-8280">
                      <span data-id="element-8281">Continuă lecția</span>
                      <ArrowRightIcon className="w-4 h-4" data-id="element-8282" />
                    </Link>
                  </div>
                </div>
              </div>}

            {!currentLesson && progress === 100 && <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30" data-id="element-8283">
                <div className="flex items-center space-x-3" data-id="element-8284">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg" data-id="element-8285">
                    <CheckCircleIcon className="w-5 h-5 text-white" data-id="element-8286" />
                  </div>
                  <div className="flex-1" data-id="element-8287">
                    <h3 className="font-bold text-white mb-1" data-id="element-8288">
                      Felicitări! 🎉
                    </h3>
                    <p className="text-sm text-white/90" data-id="element-8289">
                      Ai completat toate training-urile disponibile!
                    </p>
                  </div>
                </div>
              </div>}
          </div>
        </motion.div>

        {/* Badges Section */}
        <BadgesSection data-id="element-8290" />

        {/* Certificates Section */}
        <CertificatesSection data-id="element-8291" />

        {/* Quick Actions Grid - Mobile Optimized */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="mb-6 sm:mb-8" data-id="element-8292">
          <h2 className="text-xl sm:text-2xl font-bold text-[#132C53] mb-4 sm:mb-6" data-id="element-8293">
            Acțiuni Rapide
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-8294">
            {/* Continue Training */}
            <Link to="/cursuri" className="group" data-id="element-8295">
              <div className="bg-gradient-to-br from-[#357BEB] to-[#2868d8] rounded-xl sm:rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-1 min-h-[160px] flex flex-col" data-id="element-8296">
                <div className="flex items-start justify-between mb-4" data-id="element-8297">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center" data-id="element-8298">
                    <GraduationCapIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" data-id="element-8299" />
                  </div>
                  <ArrowRightIcon className="w-5 h-5 text-white/80 group-hover:translate-x-1 transition-transform" data-id="element-8300" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2" data-id="element-8301">
                  Continuă Training
                </h3>
                <p className="text-white/80 text-sm mt-auto" data-id="element-8302">
                  {progress < 100 ? `${100 - progress}% rămas până la finalizare` : 'Training complet!'}
                </p>
              </div>
            </Link>

            {/* Support & Resources */}
            <Link to="/support" className="group" data-id="element-8303">
              <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl sm:rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-1 min-h-[160px] flex flex-col" data-id="element-8304">
                <div className="flex items-start justify-between mb-4" data-id="element-8305">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center" data-id="element-8306">
                    <HeadphonesIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" data-id="element-8307" />
                  </div>
                  <ArrowRightIcon className="w-5 h-5 text-white/80 group-hover:translate-x-1 transition-transform" data-id="element-8308" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2" data-id="element-8309">
                  Suport & Resurse
                </h3>
                <p className="text-white/80 text-sm mt-auto" data-id="element-8310">
                  Asistență, FAQ și resurse utile
                </p>
              </div>
            </Link>

            {/* View Transactions */}
            <Link to="/tranzactii" className="group sm:col-span-2 lg:col-span-1" data-id="element-8311">
              <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl sm:rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-1 min-h-[160px] flex flex-col" data-id="element-8312">
                <div className="flex items-start justify-between mb-4" data-id="element-8313">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center" data-id="element-8314">
                    <FileTextIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" data-id="element-8315" />
                  </div>
                  <ArrowRightIcon className="w-5 h-5 text-white/80 group-hover:translate-x-1 transition-transform" data-id="element-8316" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2" data-id="element-8317">
                  Tranzacții & Simulare
                </h3>
                <p className="text-white/80 text-sm mt-auto" data-id="element-8318">
                  {transactions.length} tranzacții simulate
                </p>
              </div>
            </Link>
          </div>
        </motion.div>

        {/* Module Access Cards - Mobile Optimized */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8" data-id="element-8319">
          {/* Rental Module Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} data-id="element-8320">
            <Link to={rentalModuleUnlocked ? '/inchiriere' : '/modul-inchiriere'} className="block group" data-id="element-8321">
              <div className="relative bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg border-2 border-gray-200 hover:border-blue-300 hover:shadow-2xl transition-all overflow-hidden min-h-[200px] sm:min-h-auto" data-id="element-8322">
                {!rentalModuleUnlocked && <div className="absolute top-3 right-3 sm:top-4 sm:right-4 w-9 h-9 sm:w-10 sm:h-10 bg-slate-900/80 backdrop-blur-sm rounded-full flex items-center justify-center" data-id="element-8323">
                    <LockIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" data-id="element-8324" />
                  </div>}

                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" data-id="element-8325">
                  <HomeIcon className="w-7 h-7 sm:w-8 sm:h-8 text-white" data-id="element-8326" />
                </div>

                <h3 className="text-xl sm:text-2xl font-bold text-[#132C53] mb-2" data-id="element-8327">
                  Modul Închiriere
                </h3>
                <p className="text-gray-600 text-sm sm:text-base mb-4" data-id="element-8328">
                  {rentalModuleUnlocked ? 'Acces complet la toate funcționalitățile' : 'Finalizează trainingul pentru a debloca'}
                </p>

                <div className="flex items-center justify-between" data-id="element-8329">
                  {rentalModuleUnlocked ? <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl text-xs sm:text-sm font-bold" data-id="element-8330">
                      ✓ Deblocat
                    </span> : <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-100 text-gray-600 rounded-xl text-xs sm:text-sm font-bold" data-id="element-8331">
                      Blocat
                    </span>}
                  <ArrowRightIcon className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" data-id="element-8332" />
                </div>
              </div>
            </Link>
          </motion.div>

          {/* Sale Module Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} data-id="element-8333">
            <Link to={saleModuleUnlocked ? '/vanzare' : '/modul-vanzare'} className="block group" data-id="element-8334">
              <div className="relative bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg border-2 border-gray-200 hover:border-emerald-300 hover:shadow-2xl transition-all overflow-hidden min-h-[200px] sm:min-h-auto" data-id="element-8335">
                {!saleModuleUnlocked && <div className="absolute top-3 right-3 sm:top-4 sm:right-4 w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-amber-600 to-orange-600 rounded-full flex items-center justify-center shadow-lg" data-id="element-8336">
                    <LockIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" data-id="element-8337" />
                  </div>}

                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" data-id="element-8338">
                  <TrendingUpIcon className="w-7 h-7 sm:w-8 sm:h-8 text-white" data-id="element-8339" />
                </div>

                <h3 className="text-xl sm:text-2xl font-bold text-[#132C53] mb-2" data-id="element-8340">
                  Modul Vânzare
                </h3>
                <p className="text-gray-600 text-sm sm:text-base mb-4" data-id="element-8341">
                  {saleModuleUnlocked ? 'Nivel premium - comisioane 3.500€+' : 'Modul premium - necesită training complet'}
                </p>

                <div className="flex items-center justify-between" data-id="element-8342">
                  {saleModuleUnlocked ? <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl text-xs sm:text-sm font-bold" data-id="element-8343">
                      ✓ Deblocat
                    </span> : <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-900 rounded-xl text-xs sm:text-sm font-bold" data-id="element-8344">
                      Premium
                    </span>}
                  <ArrowRightIcon className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" data-id="element-8345" />
                </div>
              </div>
            </Link>
          </motion.div>
        </div>

        {/* Transactions Summary - Mobile Optimized */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg border border-gray-200" data-id="element-8346">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-2 sm:space-y-0" data-id="element-8347">
            <h2 className="text-xl sm:text-2xl font-bold text-[#132C53]" data-id="element-8348">
              Tranzacții Luna Aceasta
            </h2>
            <Link to="/tranzactii" className="text-[#357BEB] hover:text-[#2868d8] font-medium flex items-center space-x-1 group text-sm sm:text-base" data-id="element-8349">
              <span data-id="element-8350">Vezi toate</span>
              <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-8351" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" data-id="element-8352">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl sm:rounded-2xl p-5 sm:p-6 border-2 border-blue-100" data-id="element-8353">
              <div className="flex items-center justify-between" data-id="element-8354">
                <div data-id="element-8355">
                  <p className="text-xs sm:text-sm text-gray-600 mb-1" data-id="element-8356">
                    Total Tranzacții
                  </p>
                  <p className="text-3xl sm:text-4xl font-bold text-[#132C53]" data-id="element-8357">
                    {monthlyStats.count}
                  </p>
                </div>
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center" data-id="element-8358">
                  <FileTextIcon className="w-6 h-6 sm:w-7 sm:h-7 text-white" data-id="element-8359" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl sm:rounded-2xl p-5 sm:p-6 border-2 border-emerald-100" data-id="element-8360">
              <div className="flex items-center justify-between" data-id="element-8361">
                <div data-id="element-8362">
                  <p className="text-xs sm:text-sm text-gray-600 mb-1" data-id="element-8363">
                    Comision Generat
                  </p>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#21A366]" data-id="element-8364">
                    {monthlyStats.totalCommission.toLocaleString('ro-RO', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                    €
                  </p>
                </div>
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center" data-id="element-8365">
                  <TrendingUpIcon className="w-6 h-6 sm:w-7 sm:h-7 text-white" data-id="element-8366" />
                </div>
              </div>
            </div>
          </div>

          {transactions.length === 0 && <div className="mt-6 text-center py-6 sm:py-8" data-id="element-8367">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4" data-id="element-8368">
                <FileTextIcon className="w-7 h-7 sm:w-8 sm:h-8 text-gray-400" data-id="element-8369" />
              </div>
              <p className="text-gray-600 mb-4 text-sm sm:text-base" data-id="element-8370">
                Nicio tranzacție simulată încă
              </p>
              <Link to="/tranzactii" className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#357BEB] to-[#2868d8] text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold hover:shadow-lg transition-all text-sm sm:text-base min-h-[44px]" data-id="element-8371">
                <PlayCircleIcon className="w-5 h-5" data-id="element-8372" />
                <span data-id="element-8373">Începe Simularea</span>
              </Link>
            </div>}
        </motion.div>
      </div>
    </div>;
}